import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import BottomNav from "../components/BottomNav";
import { formatRole } from "../utils/formatters";
import type { Role } from "../types";

interface Person { id: number; name: string; employee_number: string; email: string; phone: string | null; job_title: string | null; site_id: number; department_id: number | null; role: Role; }

export default function DirectoryPage() {
  const { t } = useTranslation();
  const [people, setPeople] = useState<Person[]>([]);
  const [search, setSearch] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const qs = search ? `?search=${encodeURIComponent(search)}` : "";
      void api.get<Person[]>(`/api/org/directory${qs}`)
        .then((data) => { if (!ctrl.signal.aborted) setPeople(data); })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <h1 className="text-2xl font-semibold text-gray-900">{t("nav.directory")}</h1>
      </header>
      <div className="px-5 pb-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} className="input" />
      </div>
      <main className="flex-1 overflow-y-auto px-5 pb-24">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {people.map((p) => (
            <div key={p.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pme-blue text-sm font-bold text-white">
                  {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                  <p className="truncate text-xs text-gray-500">{p.job_title ?? formatRole(p.role)}</p>
                </div>
              </div>
              <div className="mt-2 space-y-0.5 text-xs text-gray-600">
                <p>{p.email}</p>
                {p.phone && <p>{p.phone}</p>}
                <p className="text-gray-400">{p.employee_number}</p>
              </div>
            </div>
          ))}
        </div>
        {people.length === 0 && <p className="mt-4 text-center text-sm text-gray-400">{t("common.noData")}</p>}
      </main>
      <BottomNav />
    </div>
  );
}
